import "./site/error-beacon";
import { Quickdraw, QuickdrawEvent } from "./games/quickdraw/quickdraw";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import { getActiveChallengeCode, acceptChallenge, createChallenge, fetchChallenge } from "./site/challenge";
import "./ui/styles.css";

const PB_KEY = "aether.quickdraw_pb";
const HANDLE_KEY = "aether.handle";

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startQuickdrawRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/quickdraw", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

/** v5(b): client-supplied extras carried with the submit body. mode +
 *  input_modality + first_input_delay_ms feed into scores.extra. All
 *  optional; the server soft-validates and silently drops malformed. */
interface FinishExtras {
  mode: "ranked" | "challenge";
  input_modality: "keyboard" | "swipe" | null;
  first_input_delay_ms: number | null;
}

async function finishQuickdrawRun(
  runId: string,
  handle: string,
  events: QuickdrawEvent[],
  extras: FinishExtras,
): Promise<{
  rank: number;
  total: number;
  score: number;
  latency_p50_ms?: number;
  latency_p90_ms?: number;
  latency_std_ms?: number;
  /** v5(b) schema-v2 response fields used by the game-over card. */
  first_break_idx?: number;
  first_half_avg_ms?: number;
  second_half_avg_ms?: number;
  combo_second_tap_delta_ms?: number;
} | null> {
  try {
    const res = await fetch(`/api/runs/quickdraw/${runId}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ handle, events, ...extras }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/** v5(b): characterize the warm-up-vs-fatigue arc on the game-over
 *  card. Per docs/dossier-games/07-quickdraw/07-telemetry.md §6.5. */
function driftTag(firstHalfMs: number, secondHalfMs: number): string {
  if (firstHalfMs <= 0 || secondHalfMs <= 0) return "";
  const delta = secondHalfMs - firstHalfMs;
  const abs = Math.abs(delta);
  if (abs < 12) return "steady cadence";                          // < 12ms = within HUD sample noise
  if (delta < 0) return `warmed up · ${abs}ms faster late`;       // second half faster
  return `fatigued · ${abs}ms slower late`;                       // second half slower
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudCue = document.getElementById("hud-cue")!;
const hudLast = document.getElementById("hud-last")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const glyphToggle = document.getElementById("qd-glyph-mode") as HTMLInputElement;
const histRoot = document.getElementById("qd-histogram") as HTMLElement;
const histBars = histRoot.querySelector(".qd-hist-bars") as HTMLElement;

/**
 * v5(c): the curved-line glyph toggle persists under aether.settings so
 * a player's preference carries across games + sessions. Read once at
 * page load to seed the checkbox, then again at every startRun so the
 * mid-session toggle takes effect on the next run.
 */
const SETTINGS_KEY = "aether.settings";
interface AetherSettings { audioVolume?: number; muted?: boolean; quickdrawProceduralGlyph?: boolean }
function readSettings(): AetherSettings {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}") as AetherSettings; }
  catch { return {}; }
}
function writeSettings(patch: Partial<AetherSettings>) {
  const next = { ...readSettings(), ...patch };
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* swallow */ }
}
glyphToggle.checked = readSettings().quickdrawProceduralGlyph === true;
glyphToggle.addEventListener("change", () => {
  writeSettings({ quickdrawProceduralGlyph: glyphToggle.checked });
});

/**
 * v5(b): paint the reaction-time histogram on game-over. Buckets are
 * client-derived for the immediate feedback (matches what the server
 * will compute from the same events), labels and order mirror the
 * dossier's per-cue bucket spec. Bars scale to the largest bucket so
 * a 30-cue all-lightning run still uses the full bar width.
 */
const HIST_ORDER: Array<{ key: string; label: string; range: string }> = [
  { key: "lightning", label: "Lightning", range: "≤120 ms" },
  { key: "quick",     label: "Quick",     range: "≤180 ms" },
  { key: "clean",     label: "Clean",     range: "≤240 ms" },
  { key: "late",      label: "Late",      range: "≤300 ms" },
  { key: "miss",      label: "Miss",      range: ">300 / off" },
];
function renderHistogram(buckets: Record<string, number>) {
  const max = Math.max(1, ...HIST_ORDER.map((b) => buckets[b.key] ?? 0));
  histBars.innerHTML = HIST_ORDER.map((b) => {
    const count = buckets[b.key] ?? 0;
    const pct = Math.round((count / max) * 100);
    return `<li data-bucket="${b.key}">
      <span class="qd-hist-label">${b.label} <small>${b.range}</small></span>
      <span class="qd-hist-track"><span class="qd-hist-fill" style="width:${pct}%"></span></span>
      <span class="qd-hist-count">${count}</span>
    </li>`;
  }).join("");
  histRoot.hidden = false;
}
function hideHistogram() {
  histRoot.hidden = true;
  histBars.innerHTML = "";
}

let game: Quickdraw | null = null;
let activeRunId: string | null = null;
let activeSeed: number | null = null;
let pendingEvents: QuickdrawEvent[] | null = null;
let pendingFinalScore: number | null = null;
// v5(d): stash onboarding signals so the deferred-submit path (player
// enters handle in the form after game-over) sends the same modality +
// first-input-delay as the instant-submit path would have.
let pendingInputModality: "keyboard" | "swipe" | null = null;
let pendingFirstInputDelayMs: number | null = null;

// v4 head-to-head: if ?challenge=CODE is in URL, fetch the challenge so
// we can show "Beat X's score of Y" and auto-post the accept on game-over.
const challengeCode = getActiveChallengeCode();
let challengeInfo: Awaited<ReturnType<typeof fetchChallenge>> = null;
if (challengeCode) {
  fetchChallenge(challengeCode).then((v) => {
    challengeInfo = v;
    if (v && !v.expired && !v.accepter) {
      msgBody.textContent = `${v.challenger.handle} challenges you — beat ${v.challenger.score.toLocaleString()}.`;
    }
  });
}

async function startRun() {
  if (game) game.dispose();
  // Prime audio synchronously inside the gesture.
  // (Constructor doesn't call audio.arm() — we arm here before any await.)
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startQuickdrawRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);
  activeSeed = seed;

  const proceduralGlyph = readSettings().quickdrawProceduralGlyph === true;
  game = new Quickdraw(canvas, {
    onHud: (cuesDone, lastBucket, scoreEstimate) => {
      hudCue.textContent = `${cuesDone} / 30`;
      hudLast.textContent = lastBucket;
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async ({ events, scoreEstimate, bestStreak, bucketsByName, combosHit, standoffsHit, phrasesCompleted, inputModality, firstInputDelayMs }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      renderHistogram(bucketsByName);
      const summary = `${bucketsByName.lightning}⚡ · ${bucketsByName.quick}⌁ · ${bucketsByName.clean}· · ${bucketsByName.miss}✕`;
      const comboTag = combosHit > 0 ? ` · ${combosHit}⚡⚡ combos` : "";
      const standoffTag = standoffsHit > 0 ? ` · ${standoffsHit}🤠 standoffs` : "";
      const phraseTag = phrasesCompleted > 0 ? ` · ${phrasesCompleted}♫ phrases` : "";
      msgBody.textContent = `${scoreEstimate.toLocaleString()} pts · ${summary} · best streak ${bestStreak}${comboTag}${standoffTag}${phraseTag}`;
      btnStart.textContent = "Try again";
      // PB tracking
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* swallow */ }
      const xp = recordRun({ game: "quickdraw", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("quickdraw", scoreEstimate);

      // Submit if we have a run + saved handle; otherwise stash & prompt.
      pendingFinalScore = scoreEstimate;
      if (!activeRunId) {
        renderChallengeFooter(scoreEstimate, null);
        return;
      }
      // v5(b): mode is challenge if a challenge code is active, ranked
      // otherwise.
      // v5(d): input_modality + first_input_delay_ms now come from the
      // Quickdraw game class (first qualifying input fires both).
      const extras: FinishExtras = {
        mode: challengeCode ? "challenge" : "ranked",
        input_modality: inputModality,
        first_input_delay_ms: firstInputDelayMs,
      };
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishQuickdrawRun(activeRunId, handle, events, extras);
        if (res) {
          msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
          // v5.1: server-derived latency telemetry (p50 / p90 / σ) lands
          // in scores.extra. Surface a compact one-liner so the player
          // sees the same shape the leaderboard will store.
          if (res.latency_p50_ms !== undefined) {
            msgBody.textContent +=
              ` · p50 ${res.latency_p50_ms}ms · p90 ${res.latency_p90_ms}ms · σ ${res.latency_std_ms}ms`;
          }
          // v5(b): warm-up-vs-fatigue arc tag.
          if (res.first_half_avg_ms !== undefined && res.second_half_avg_ms !== undefined) {
            const tag = driftTag(res.first_half_avg_ms, res.second_half_avg_ms);
            if (tag) msgBody.textContent += ` · ${tag}`;
          }
          // v5(b): first-break-idx tag — only render when a break occurred.
          if (typeof res.first_break_idx === "number" && res.first_break_idx >= 0) {
            msgBody.textContent += ` · streak broke at cue ${res.first_break_idx + 1}`;
          }
        }
        await handleChallengeOnGameOver(handle, scoreEstimate, activeRunId);
        renderChallengeFooter(scoreEstimate, handle);
      } else {
        pendingEvents = events;
        pendingInputModality = inputModality;
        pendingFirstInputDelayMs = firstInputDelayMs;
        submitForm.hidden = false;
        handleInputOverlay.value = "";
      }
    },
  }, seed, { proceduralGlyph });

  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }

  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Quickdraw";
  msgBody.textContent = "";
  hideHistogram();
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingEvents) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const extras: FinishExtras = {
    mode: challengeCode ? "challenge" : "ranked",
    input_modality: pendingInputModality,
    first_input_delay_ms: pendingFirstInputDelayMs,
  };
  const res = await finishQuickdrawRun(activeRunId, value, pendingEvents, extras);
  pendingEvents = null;
  pendingInputModality = null;
  pendingFirstInputDelayMs = null;
  submitForm.hidden = true;
  if (res) {
    msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
    if (res.first_half_avg_ms !== undefined && res.second_half_avg_ms !== undefined) {
      const tag = driftTag(res.first_half_avg_ms, res.second_half_avg_ms);
      if (tag) msgBody.textContent += ` · ${tag}`;
    }
    if (typeof res.first_break_idx === "number" && res.first_break_idx >= 0) {
      msgBody.textContent += ` · streak broke at cue ${res.first_break_idx + 1}`;
    }
  }
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});

// v4: head-to-head challenges.

async function handleChallengeOnGameOver(handle: string, score: number, runId: string) {
  if (!challengeCode || !challengeInfo || challengeInfo.expired) return;
  if (challengeInfo.accepter) return; // already settled
  const result = await acceptChallenge(challengeCode, handle, score, runId);
  if (!result) return;
  const verdict =
    result.winner === handle ? `🏆 YOU WIN — you outscored ${result.challenger.handle} by ${(score - result.challenger.score).toLocaleString()} pts`
    : result.winner === "tie" ? `It's a tie — exactly ${score.toLocaleString()} pts both`
    : `${result.challenger.handle} wins — ${(result.challenger.score - score).toLocaleString()} pts ahead`;
  msgBody.textContent += ` · ${verdict}`;
}

function renderChallengeFooter(score: number, handle: string | null) {
  if (challengeCode) return; // already in a challenge — don't offer a new one
  // Add a "Challenge a friend" button below the existing UI.
  let footer = document.getElementById("chal-footer");
  if (!footer) {
    footer = document.createElement("div");
    footer.id = "chal-footer";
    footer.className = "chal-footer";
    document.querySelector(".screen-msg")?.appendChild(footer);
  }
  if (!handle) {
    footer.innerHTML = `<small>Set a handle to share a head-to-head challenge with this run's seed.</small>`;
    return;
  }
  if (!activeSeed) {
    footer.innerHTML = `<small>Challenge sharing is unavailable for this run.</small>`;
    return;
  }
  footer.innerHTML = `<button type="button" class="btn ghost chal-btn" id="btn-challenge">Challenge a friend →</button><span class="chal-status" id="chal-status"></span>`;
  const btn = document.getElementById("btn-challenge") as HTMLButtonElement;
  const status = document.getElementById("chal-status") as HTMLElement;
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    btn.textContent = "Creating…";
    const res = await createChallenge({
      game: "quickdraw",
      seed: activeSeed!,
      handle,
      score,
      runId: activeRunId ?? undefined,
    });
    if (!res) {
      btn.disabled = false;
      btn.textContent = "Try again";
      status.textContent = "Failed to mint challenge.";
      return;
    }
    const link = `${location.origin}/challenge?code=${res.code}`;
    btn.textContent = "Copied!";
    try { await navigator.clipboard?.writeText(link); } catch { /* fallthrough */ }
    status.innerHTML = `Code <code>${res.code}</code> · <a href="${link}" target="_blank">${link}</a>`;
  });
}
